import { useEffect, useState } from "react";

import { fetchProfileShortcutsDto } from "../queries/profile-shortcuts/fetchProfileShortcuts";
import { mapProfileShortcuts } from "../queries/profile-shortcuts/mappers";
import type { ProfileShortcut } from "../types/profileShortcuts";

export type UseProfileShortcutsResult = {
  shortcuts: ProfileShortcut[];
  /** `true` until the initial GET settles (success or failure). */
  isLoading: boolean;
};

/**
 * Resolves the Profile Shortcuts for Configurations → Profile
 * ("Feature Quick Access") by fetching
 * `GET /admin-panel-v2/configurations/profile-shortcuts` and mapping the
 * DTOs to the UI view-model.
 *
 * Returns `{ shortcuts: [], isLoading: true }` while the request is in flight
 * so callers can show a loader instead of the empty-state message. After the
 * first response, `isLoading` is `false` and `shortcuts` is `[]` when the
 * client has none configured (or the request failed).
 */
export function useProfileShortcuts(): UseProfileShortcutsResult {
  const [shortcuts, setShortcuts] = useState<ProfileShortcut[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchProfileShortcutsDto()
      .then((dtos) => {
        if (!cancelled) setShortcuts(mapProfileShortcuts(dtos));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { shortcuts, isLoading };
}

export default useProfileShortcuts;
